import { signalAdminSessionExpired } from "../admin-session";

type GeneratedResponse<T> = {
  data: T;
  status: number;
  headers: Headers;
};

function requireSameOriginPath(url: string) {
  if (!url.startsWith("/") || url.startsWith("//")) {
    throw new Error("API requests must use a same-origin path");
  }
}

function adminEdgePath(url: string) {
  if (url === "/v1" || url.startsWith("/v1/")) return `/admin/api${url}`;
  if (url === "/admin/auth" || url.startsWith("/admin/auth/")) return url;
  throw new Error("Admin API path must use /v1 or /admin/auth");
}

function isUnauthenticatedAdminAuthFlow(url: string) {
  return url === "/admin/auth/login" || url.startsWith("/admin/auth/enrollment/");
}

async function generatedResponse<T>(response: Response): Promise<T> {
  const hasBody = ![204, 205, 304].includes(response.status);
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
  const attachment = response.ok && response.headers.get("Content-Disposition")?.toLowerCase().includes("attachment");
  const data = !hasBody
    ? {}
    : attachment
      ? await response.blob()
      : contentType.includes("json")
      ? await response.json()
      : contentType.startsWith("text/")
        ? await response.text()
        : await response.blob();
  return { data, status: response.status, headers: response.headers } as GeneratedResponse<T> as T;
}

/**
 * Runtime transport for every Orval-generated Admin API operation. The
 * generated contract owns upstream paths, request bodies, and response types;
 * this mutator maps them into the Admin edge namespace and owns same-origin
 * enforcement and session credentials.
 */
export async function adminFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  requireSameOriginPath(url);
  const headers = new Headers(options.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  headers.delete("Authorization");
  const response = await fetch(adminEdgePath(url), { ...options, credentials: "include", headers });
  if (response.status === 401 && url !== "/v1/admin/me" && !isUnauthenticatedAdminAuthFlow(url)) {
    signalAdminSessionExpired();
  }
  return generatedResponse<T>(response);
}

/**
 * Runtime transport for generated Public License API operations. Runtime
 * proof headers are preserved, while Admin session cookies are omitted.
 */
export async function publicFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  requireSameOriginPath(url);
  const headers = new Headers(options.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  const response = await fetch(url, { ...options, credentials: "omit", headers });
  return generatedResponse<T>(response);
}
